import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Menu, UtensilsCrossed } from "lucide-react";
import Sidebar, { MobileSidebar } from "./components/Sidebar";
import { AUTH_STATE_EVENT, getAuthUser } from "../AdminAuth/components/authStore";
import { env } from "../../config/env";
import NotificationsBell from "./components/NotificationsBell";
import DispatchAutoAssignFailurePopup from "../../components/Notifications/DispatchAutoAssignFailurePopup";
import { useOrderNotificationSoundAlert } from "../../hooks/useOrderNotificationSoundAlert";
import dashboardHeaderGrocery from "../../assets/dashboard-header-grocery-v2.png";

function titleFromPath(pathname) {
  if (pathname.startsWith("/admin/food-dashboard")) return "FOOD DASHBOARD";
  if (pathname.startsWith("/admin/dashboard/profile")) return "MY PROFILE";
  if (pathname.startsWith("/admin/dashboard/notifications")) return "ADMIN NOTIFICATIONS";
  if (pathname.startsWith("/admin/dashboard/user-notifications")) return "USER NOTIFICATIONS";
  if (pathname.startsWith("/admin/dashboard/support")) return "CUSTOMER SUPPORT";
  if (pathname.startsWith("/admin/dashboard/order-analytics")) return "ORDER ANALYTICS";
  if (pathname.startsWith("/admin/dashboard/payment-collections")) return "PAYMENT COLLECTIONS";
  if (pathname.startsWith("/admin/dashboard/driver-wallet")) return "DRIVER WALLET";
  if (pathname.startsWith("/admin/dashboard/delivery-partners")) return "DELIVERY PARTNERS";
  if (pathname.startsWith("/admin/dashboard/app-layout")) return "APP LAYOUT";
  if (pathname.startsWith("/admin/dashboard/image-manager")) return "IMAGE MANAGER";
  if (pathname.startsWith("/admin/dashboard/broadcast-messages")) return "BROADCAST MESSAGES";
  if (pathname.startsWith("/admin/dashboard/email-templates")) return "EMAIL TEMPLATES";
  if (pathname.startsWith("/admin/dashboard/activity-logs")) return "ACTIVITY LOGS";
  if (pathname.startsWith("/admin/dashboard/short-videos")) return "SHORT VIDEOS";
  if (pathname.startsWith("/admin/dashboard/inventory/purchase-history")) return "PURCHASE HISTORY";
  if (pathname.startsWith("/admin/dashboard/inbound-receipts")) return "INBOUND RECEIPTS";
  if (pathname.startsWith("/admin/dashboard/warehouse-sales")) return "WAREHOUSE SALES";
  if (pathname.startsWith("/admin/dashboard/customer-orders")) return "CUSTOMER ORDERS";
  if (pathname.startsWith("/admin/dashboard/catalog-import")) return "CATALOG IMPORT";
  if (pathname.startsWith("/admin/dashboard/settings")) return "SETTINGS";
  if (pathname.startsWith("/admin/dashboard/posters")) return "POSTERS";
  if (pathname.startsWith("/admin/dashboard/coupons")) return "COUPONS";
  if (pathname.startsWith("/admin/dashboard/reports")) return "BUSINESS REPORTS";
  if (pathname.startsWith("/admin/dashboard/pricing")) return "PRODUCT PRICING";
  if (pathname.startsWith("/admin/dashboard/stock")) return "STOCK MANAGEMENT";
  if (pathname.startsWith("/admin/dashboard/products")) return "PRODUCTS";
  if (pathname.startsWith("/admin/dashboard/inventory")) return "INVENTORY";
  if (pathname.startsWith("/admin/dashboard/users")) return "USERS";
  if (pathname.startsWith("/admin/dashboard/warehouse")) return "WAREHOUSE";
  if (pathname.startsWith("/admin/dashboard/employees")) return "EMPLOYEES";
  if (pathname.startsWith("/admin/dashboard/roles")) return "ROLES";
  return "DASHBOARD";
}

function getDisplayName(user) {
  return (
    user?.name ||
    user?.fullName ||
    user?.adminName ||
    user?.username ||
    user?.email?.split("@")?.[0] ||
    "Admin"
  );
}

function getDisplayRole(user) {
  return String(user?.role || "Admin")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getProfileImage(user) {
  return (
    user?.onboarding?.documents?.profilePhoto?.url ||
    user?.onboarding?.photos?.photo1?.url ||
    user?.profileImage ||
    user?.profile_image ||
    user?.avatarUrl ||
    user?.image ||
    user?.avatar ||
    ""
  );
}

function toAbsoluteFileUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = env?.AUTH_BASE_URL || "";
  return `${base}${path}`;
}

function ProfileMenu() {
  const [user, setUser] = useState(() => getAuthUser() || {});
  const navigate = useNavigate();

  useEffect(() => {
    const syncUser = () => setUser(getAuthUser() || {});

    window.addEventListener(AUTH_STATE_EVENT, syncUser);
    window.addEventListener("storage", syncUser);

    return () => {
      window.removeEventListener(AUTH_STATE_EVENT, syncUser);
      window.removeEventListener("storage", syncUser);
    };
  }, []);

  const displayName = getDisplayName(user);
  const displayRole = getDisplayRole(user);
  const profileImage = toAbsoluteFileUrl(getProfileImage(user));
  const initial = displayName?.charAt(0)?.toUpperCase() || "A";

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={() => navigate("/admin/dashboard/profile")}
        className="flex h-12 items-center gap-2.5 rounded-[17px] border border-white/90 bg-white/80 px-2.5 shadow-[0_8px_22px_rgba(37,48,78,0.07)] ring-1 ring-slate-200/70 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-[#f2c6cc] hover:bg-white sm:h-[54px] sm:px-3"
      >
        <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-[#f2c6cc] bg-[linear-gradient(135deg,#ffe8ed,#ffd3dc)] sm:h-10 sm:w-10">
          {profileImage ? (
            <img
              src={profileImage}
              alt="Profile"
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-[15px] font-bold text-[#d86a78]">
              {initial}
            </span>
          )}
        </div>

        <div className="hidden text-left md:block">
          <div className="text-[10px] font-medium text-slate-400">Logged in as</div>
          <div className="max-w-[112px] truncate text-[12px] font-black text-slate-800">
            {displayName}
          </div>
        </div>
        <ChevronDown size={14} className="hidden text-slate-700 transition-transform duration-300 group-hover:rotate-180 md:block" />
      </button>

      <div className="pointer-events-none absolute right-0 top-[calc(100%+10px)] z-50 w-[280px] translate-y-2 rounded-[18px] border border-[#f2c6cc] bg-white p-5 opacity-0 shadow-[0_20px_50px_rgba(0,0,0,0.12)] transition-all duration-200 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-[#f2c6cc] bg-[#d86a78]/10">
            {profileImage ? (
              <img
                src={profileImage}
                alt="Profile"
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-[22px] font-bold text-[#d86a78]">
                {initial}
              </span>
            )}
          </div>

          <div className="min-w-0">
            <div className="truncate text-[16px] font-bold text-[#d86a78]">
              {displayName}
            </div>
            <div className="mt-1 text-[13px] text-slate-500">{displayRole}</div>
          </div>
        </div>

        <div className="my-4 h-px bg-[#f2c6cc]" />

        <div className="space-y-3 text-sm">
          <div>
            <div className="text-[11px] uppercase text-slate-400">Email</div>
            <div className="truncate text-slate-700">{user?.email || "-"}</div>
          </div>

          <div>
            <div className="text-[11px] uppercase text-slate-400">Role</div>
            <div className="text-slate-700">{displayRole}</div>
          </div>

          <div>
            <div className="text-[11px] uppercase text-slate-400">Status</div>
            <div className="font-medium text-green-600">
              {user?.status || "Active"}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <button
            type="button"
            onClick={() => navigate("/admin/dashboard/profile")}
            className="w-full rounded-[12px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-2 text-sm font-semibold text-[#d86a78] transition hover:bg-white"
          >
            My Profile
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  useOrderNotificationSoundAlert({ source: "admin" });

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("adminSidebarCollapsed") === "1";
  });
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitle = titleFromPath(location.pathname);

  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem("adminSidebarCollapsed", next ? "1" : "0");
      } catch {}
      return next;
    });
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f7f9fc]">
      <DispatchAutoAssignFailurePopup mode="admin" />
      <Sidebar collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} />

      <MobileSidebar open={mobileOpen} onClose={() => setMobileOpen(false)} />

      <main className={`min-h-screen bg-[radial-gradient(circle_at_75%_0%,rgba(244,114,182,0.06),transparent_22%),linear-gradient(180deg,#f9fbfe_0%,#f6f8fc_100%)] transition-[padding] duration-300 ${sidebarCollapsed ? "xl:pl-[78px]" : "xl:pl-[232px]"}`}>
        <header className={`fixed left-0 right-0 top-0 z-30 bg-[#f7f9fc]/90 p-2 backdrop-blur-xl transition-[left] duration-300 sm:p-2.5 ${sidebarCollapsed ? "xl:left-[78px]" : "xl:left-[232px]"}`}>
          <div className="relative flex h-[68px] items-center gap-3 rounded-[22px] border border-white/90 bg-[linear-gradient(105deg,rgba(255,255,255,0.96)_0%,rgba(255,249,250,0.94)_58%,rgba(255,246,240,0.92)_100%)] px-3 shadow-[0_10px_30px_rgba(37,48,78,0.07)] ring-1 ring-slate-200/70 sm:h-[76px] sm:px-4 xl:px-5">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-[22px]">
              <div className="absolute -right-16 -top-24 h-56 w-56 rounded-full bg-rose-200/20 blur-3xl" />
              <img src={dashboardHeaderGrocery} alt="" className="absolute -top-[17px] left-[250px] hidden w-[345px] max-w-none opacity-90 drop-shadow-[0_8px_16px_rgba(80,48,45,0.07)] xl:block 2xl:left-[300px]" />
            </div>
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="relative z-10 grid h-11 w-11 shrink-0 place-items-center rounded-[15px] border border-rose-100 bg-white/90 text-[#ee5870] shadow-[0_7px_18px_rgba(216,106,120,0.10)] transition-all hover:-translate-y-0.5 hover:bg-[#fff6f7] sm:h-[52px] sm:w-[52px] xl:hidden"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>

            <span className="relative z-10 hidden h-10 w-px bg-slate-200/80 sm:block xl:hidden" />

            <div className="relative z-10 min-w-0">
              <div className="truncate text-[14px] font-black tracking-[-0.01em] text-[#101828] sm:text-[17px]">
                <span className="text-[#d86a78]">{pageTitle}</span>
              </div>
              <div className="hidden text-[11px] font-semibold text-slate-400 sm:block">Nefazo Admin</div>
            </div>

            <div className="relative z-10 ml-auto flex items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => navigate("/admin/food-dashboard")}
                className="group/food inline-flex h-11 items-center gap-2 rounded-[15px] border border-rose-200/80 bg-gradient-to-r from-rose-50/90 to-white/90 px-3 text-[11px] font-black text-[#e34661] shadow-[0_8px_20px_rgba(225,91,116,0.10)] backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_12px_26px_rgba(225,91,116,0.16)] sm:h-[54px] sm:gap-3 sm:px-4"
                title="Open Food Admin Dashboard"
              >
                <span className="grid h-8 w-8 place-items-center rounded-[11px] bg-[#ffe9ed] text-[#e94864] sm:h-9 sm:w-9"><UtensilsCrossed size={16} /></span>
                <span className="hidden lg:inline">Food Dashboard</span>
                <ArrowRight size={16} className="hidden transition-transform duration-300 group-hover/food:translate-x-1 lg:block" />
              </button>
              <span className="hidden h-10 w-px bg-slate-200/80 md:block" />
              <NotificationsBell />
              <span className="hidden h-10 w-px bg-slate-200/80 md:block" />
              <ProfileMenu />
            </div>
          </div>
        </header>

        <div className="mx-auto w-full max-w-[1920px] px-3 pb-4 pt-[100px] sm:px-5 sm:pb-5 sm:pt-[116px] xl:px-7 xl:pb-6 xl:pt-[120px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
